/* eslint-disable max-lines-per-function */
import {
  Autocomplete,
  AutocompleteChanges,
  Banner,
  Button,
  Icon,
  Input,
  Label,
  Typography,
} from '@equinor/eds-core-react';
import { error_filled as ERROR_FILLED } from '@equinor/eds-icons';
import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import {
  AddAnalogueModelAreaCommandForm,
  ComputeJobStatus,
  CoordinateDto,
  ModelAreaTypeDto,
} from '../../../../api/generated';
import { useMutateAreaCoordinates } from '../../../../hooks/useMutateAreaCoordinates';
import { analogueModelDefault } from '../../../../constants/analogueModelDefault';
import { useFetchModel } from '../../../../hooks/useFetchModel';
import { useFetchModelAreas } from '../../../../hooks/useFetchModelAreas';
import { useFetchCases } from '../../../../hooks/useFetchCases';
import { useModelAreasStore } from '../hooks/useModelAreasStore';
import * as Styled from './AreaForm.styled';

interface FormState {
  modelAreaId: string;
  coordinates: CoordinateDto[];
}

const DEFAULT_FORM: FormState = {
  modelAreaId: '',
  coordinates: [
    { x: 0, y: 0, m: 0 },
    { x: 0, y: 0, m: 1 },
  ],
};

const DEFAULT_AREA: ModelAreaTypeDto = {
  modelAreaTypeId: '',
  name: '',
  description: '',
};

export type CoordinateErrors = {
  area?: string;
  x0?: string;
  y0?: string;
  x1?: string;
  y1?: string;
};

function validate(
  form: FormState,
  activeArea: ModelAreaTypeDto,
): CoordinateErrors {
  const err: CoordinateErrors = {};
  if (!activeArea.modelAreaTypeId) err.area = 'You must select a model area';
  const [c0, c1] = form.coordinates;
  if (isNaN(Number(c0.x))) err.x0 = 'Must be a number';
  if (isNaN(Number(c0.y))) err.y0 = 'Must be a number';
  if (isNaN(Number(c1.x))) err.x1 = 'Must be a number';
  if (isNaN(Number(c1.y))) err.y1 = 'Must be a number';
  if (!err.x0 && !err.x1 && Number(c0.x) >= Number(c1.x))
    err.x0 = 'X start must be less than X end';
  if (!err.y0 && !err.y1 && Number(c0.y) >= Number(c1.y))
    err.y0 = 'Y start must be less than Y end';
  return err;
}

function coordsToBbox(coords: CoordinateDto[]) {
  const [c0, c1] = coords;
  const x0 = Number(c0?.x),
    y0 = Number(c0?.y);
  const x1 = Number(c1?.x),
    y1 = Number(c1?.y);
  if (isNaN(x0) || isNaN(y0) || isNaN(x1) || isNaN(y1)) return null;
  if (x0 >= x1 || y0 >= y1) return null;
  return { x0, y0, x1, y1 };
}

interface CoordInputProps {
  label: string;
  value: string;
  error: string | undefined;
  disabled: boolean;
  onChange: (value: string) => void;
}

const CoordInput = ({
  label,
  value,
  error,
  disabled,
  onChange,
}: CoordInputProps) => (
  <div>
    <Label label={label} />
    <Input
      value={value}
      onChange={(e: React.FormEvent<HTMLInputElement>) =>
        onChange(e.currentTarget.value)
      }
      rightAdornments={
        error ? <Icon data={ERROR_FILLED} color="red" size={24} /> : 'm'
      }
      variant={error ? 'error' : undefined}
      disabled={disabled}
    />
  </div>
);

interface AreaFormProps {
  onSaved: () => void;
}

export const AreaForm = ({ onSaved }: AreaFormProps) => {
  const { data: analogueModel = analogueModelDefault } = useFetchModel();
  const { data: modelAreaTypes = [] } = useFetchModelAreas();
  const { data: computeCases = [] } = useFetchCases();
  const mutate = useMutateAreaCoordinates();

  const storeBbox = useModelAreasStore((s) => s.bbox);
  const setBbox = useModelAreasStore((s) => s.setBbox);
  const editing = useModelAreasStore((s) => s.editing);
  const setEditing = useModelAreasStore((s) => s.setEditing);

  const [activeArea, setActiveArea] = useState<ModelAreaTypeDto>(DEFAULT_AREA);
  const [form, setForm] = useState<FormState>(cloneDeep(DEFAULT_FORM));
  const [savedForm, setSavedForm] = useState<FormState>(
    cloneDeep(DEFAULT_FORM),
  );
  const [isNew, setIsNew] = useState(false);
  const [errors, setErrors] = useState<CoordinateErrors>({});

  const hasResults = computeCases
    .filter((c) => c.modelArea !== null)
    .filter((c) => c.modelArea.name === activeArea.name)
    .some(
      (c) =>
        c.jobStatus === ComputeJobStatus.RUNNING ||
        c.jobStatus === ComputeJobStatus.SUCCEEDED ||
        c.jobStatus === ComputeJobStatus.WAITING,
    );

  // When canvas bbox changes externally (canvas drag/resize), sync into inputs
  useEffect(() => {
    if (!storeBbox) return;
    setForm((prev) => ({
      ...prev,
      coordinates: [
        { x: Math.round(storeBbox.x0), y: Math.round(storeBbox.y0), m: 0 },
        { x: Math.round(storeBbox.x1), y: Math.round(storeBbox.y1), m: 1 },
      ],
    }));
  }, [storeBbox]);

  const handleSelectChange = (
    changes: AutocompleteChanges<ModelAreaTypeDto>,
  ) => {
    setErrors({});
    setEditing(false);
    if (changes.selectedItems.length === 0) {
      setActiveArea(DEFAULT_AREA);
      setForm(cloneDeep(DEFAULT_FORM));
      setBbox(null);
      return;
    }
    const selected = changes.selectedItems[0];
    const existing = analogueModel.modelAreas?.find(
      (a) => a.modelAreaType === selected.name,
    );
    if (existing) {
      const m0 = existing.coordinates.find((c) => c.m === 0);
      const m1 = existing.coordinates.find((c) => c.m === 1);
      if (!m0 || !m1) return;
      const loaded: FormState = {
        modelAreaId: existing.modelAreaId,
        coordinates: [m0, m1],
      };
      setForm(loaded);
      setSavedForm(cloneDeep(loaded));
      setIsNew(false);
      setBbox({
        x0: Math.min(Number(m0.x), Number(m1.x)),
        x1: Math.max(Number(m0.x), Number(m1.x)),
        y0: Math.min(Number(m0.y), Number(m1.y)),
        y1: Math.max(Number(m0.y), Number(m1.y)),
      });
    } else {
      const blank = cloneDeep(DEFAULT_FORM);
      setForm(blank);
      setSavedForm(cloneDeep(blank));
      setIsNew(true);
      setBbox(null);
    }
    setActiveArea(selected);
  };

  const setCoord = (index: number, axis: 'x' | 'y', value: string) => {
    const updated = cloneDeep(form);
    updated.coordinates[index] = {
      ...updated.coordinates[index],
      [axis]: value,
    };
    setForm(updated);
    // Live-update canvas if all 4 values are now valid
    const bbx = coordsToBbox(updated.coordinates);
    if (bbx) setBbox(bbx);
  };

  const handleSave = async () => {
    const errs = validate(form, activeArea);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    if (isNew) {
      const body: AddAnalogueModelAreaCommandForm = {
        modelAreaTypeId: activeArea.modelAreaTypeId,
        coordinates: form.coordinates,
      };
      const res = await mutate.postAreaCoordinates.mutateAsync({
        id: analogueModel.analogueModelId,
        requestBody: body,
      });
      if (res.data?.success) {
        setEditing(false);
        setIsNew(false);
        setForm((f) => ({
          ...f,
          modelAreaId: res.data?.data.modelAreaId ?? '',
        }));
        onSaved();
      }
    } else {
      const res = await mutate.putAreaCoordinates.mutateAsync({
        id: analogueModel.analogueModelId,
        modelAreaId: form.modelAreaId,
        requestBody: { coordinates: form.coordinates },
      });
      if (res.data?.success) {
        setEditing(false);
        onSaved();
      }
    }
  };

  return (
    <Styled.Wrapper>
      <Autocomplete
        label="Select area"
        options={modelAreaTypes}
        optionLabel={(o) => o.name}
        onOptionsChange={handleSelectChange}
        selectedOptions={activeArea.modelAreaTypeId ? [activeArea] : []}
        variant={errors.area ? 'error' : undefined}
      />

      {activeArea.modelAreaTypeId && (
        <>
          {hasResults && editing && (
            <Styled.Warning>
              <Banner.Message>
                IMPORTANT - this model area has one or more results in object or
                variogram cases. Changing the coordinates will discard any
                associated result.
              </Banner.Message>
            </Styled.Warning>
          )}

          <Styled.CoordinateRow>
            <CoordInput
              label="X start"
              value={String(form.coordinates[0]?.x ?? '')}
              error={errors.x0}
              disabled={!editing}
              onChange={(v) => setCoord(0, 'x', v)}
            />
            <CoordInput
              label="X end"
              value={String(form.coordinates[1]?.x ?? '')}
              error={errors.x1}
              disabled={!editing}
              onChange={(v) => setCoord(1, 'x', v)}
            />
          </Styled.CoordinateRow>
          <Styled.CoordinateRow>
            <CoordInput
              label="Y start"
              value={String(form.coordinates[0]?.y ?? '')}
              error={errors.y0}
              disabled={!editing}
              onChange={(v) => setCoord(0, 'y', v)}
            />
            <CoordInput
              label="Y end"
              value={String(form.coordinates[1]?.y ?? '')}
              error={errors.y1}
              disabled={!editing}
              onChange={(v) => setCoord(1, 'y', v)}
            />
          </Styled.CoordinateRow>

          {Object.values(errors).some(Boolean) && (
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {Object.values(errors)
                .filter(Boolean)
                .map((e, i) => (
                  <li key={i}>
                    <Typography group="input" variant="helper">
                      {e}
                    </Typography>
                  </li>
                ))}
            </ul>
          )}

          <Styled.Actions>
            {!editing && (
              <Button variant="outlined" onClick={() => setEditing(true)}>
                Edit coordinates
              </Button>
            )}
            {editing && (
              <>
                <Button
                  onClick={handleSave}
                  disabled={
                    mutate.postAreaCoordinates.isPending ||
                    mutate.putAreaCoordinates.isPending
                  }
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const reverted = cloneDeep(savedForm);
                    setForm(reverted);
                    setEditing(false);
                    setErrors({});
                    const [c0, c1] = reverted.coordinates;
                    if (c0 && c1 && (Number(c0.x) || Number(c0.y))) {
                      setBbox({
                        x0: Math.min(Number(c0.x), Number(c1.x)),
                        x1: Math.max(Number(c0.x), Number(c1.x)),
                        y0: Math.min(Number(c0.y), Number(c1.y)),
                        y1: Math.max(Number(c0.y), Number(c1.y)),
                      });
                    } else {
                      setBbox(null);
                    }
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          </Styled.Actions>
        </>
      )}
    </Styled.Wrapper>
  );
};
